import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PopinForm from "../Popin/PopinForm.js";
import { Container, SubContainer } from "./styles/DataTable.styled.js";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Filters from "./Filters.js";
import { IconButton } from "@mui/material";
import { VisibilityOff as VisibilityOffIcon, Visibility as VisibilityIcon, Edit as EditIcon } from "@mui/icons-material";
import { getHashedIds } from "../../services/previews.js";
import { Grade as GradeIcon } from "@mui/icons-material";
import { useAuth } from "../../hooks/use-auth.js";
import { StyledDataGrid } from "./styles/DataTable.styled.js";
import { useGridApiRef } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { displaySuccessFeedback, displayErrorFeedback } from "../../app/feedbackSlice.js";
import { roles } from "../../utils/roles.js";
import Toolbar from "./Toolbar.js";

const getCustomActionCells = (row, context) => {
  const { user, roles, permissions, headCellList, openPopinUpdate, label } = context;
  const isAdminAccount = user.role === roles?.SUPERADMIN;
  const hasPermission = permissions.update.find((role) => role === user.role || role === roles.ALL);
  const isAgencyOwner = row.agencies?.[0]?.id === undefined || row.agencies?.[0]?.id === user.agencyId;

  const isExternRole = user.role === roles?.EXTERN;
  let canUpdate = (hasPermission && isAgencyOwner) || isAdminAccount;

  if (isExternRole) {
    canUpdate = row.creatorAccountType === roles?.EXTERN;
  }

  const availableEditItem = <GridActionsCellItem icon={<EditIcon />} onClick={openPopinUpdate.bind(this, row)} label={`Edit ${label}`} />;
  const editItem = canUpdate ? availableEditItem : null;
  const topProjectIcon = <GridActionsCellItem icon={<GradeIcon sx={{ color: "#F5B83D" }} />} label={`Top project`} />;
  const customActionCells = headCellList.filter((cell) => cell.cellType === "actions");

  const visibilityItem = customActionCells.reduce((acc, cell) => {
    if (cell.actionType === "link") {
      if (row.previewLink) {
        if (row.canShare) {
          return [
            ...acc,
            <a key={cell.field} aria-label="Sharable preview" href={row.previewLink} rel="noreferrer" target="_blank">
              <IconButton color="primary">
                <VisibilityIcon />
              </IconButton>
            </a>,
          ];
        } else {
          return [
            ...acc,
            <a key={cell.field} href={row.previewLink} rel="noreferrer" target="_blank">
              <IconButton disabled aria-label="Unsharable preview">
                <VisibilityOffIcon />
              </IconButton>
            </a>,
          ];
        }
      } else {
        return acc;
      }
    } else {
      return acc;
    }
  }, []);
  return [...(row.isTopProject ? [topProjectIcon] : []), editItem, ...visibilityItem].filter(Boolean);
};

const getActionCells = (context) => {
  return {
    field: "actions",
    type: "actions",
    sortable: false,
    editable: false,
    isOnTable: true,
    width: 120,
    hideable: false,
    getActions: (params) => getCustomActionCells(params.row, context),
  };
};

export default function EnhancedTable({ permissions, headCells, entity: { tableId, label }, fetchData, addData: addToList, deleteData: deleteFromList, updateData: updateList, setPopinAlert, sortModel: defaultSortModel }) {
  const [popin, setPopin] = useState({ isOpen: false, action: null, title: null });
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rowsState, setRowsState] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState(defaultSortModel ?? [{ field: "name", sort: "asc" }]);
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: "include", ids: new Set() });
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({});
  // const [isSavedSearch, setIsSavedSearch] = useState(false);
  // const [isOpenPopinSearch, setIsOpenPopinSearch] = useState(false);
  const [triggerSearch, setTriggerSearch] = useState(false);
  const filtersRef = useRef(filters);
  const { getUserType, user } = useAuth();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();

  const hasFilter = useMemo(() => !!headCells.find(({ isOnFilter }) => isOnFilter), [headCells]);
  const userType = getUserType();

  const headCellList = useMemo(() => headCells.filter((cell) => !cell.isTest || (userType === "TEST" && cell.isTest)), [headCells, userType]);

  const handleFiltersList = useCallback((newFilters) => {
    setFilters((prev) => {
      const updated = { ...prev, ...newFilters };

      Object.keys(updated).forEach((key) => {
        if (updated[key] === "" || updated[key] == null) {
          delete updated[key];
        }
      });

      return updated;
    });
  }, []);

  useEffect(() => {
    if (triggerSearch) {
      getData();
      setTriggerSearch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSearch]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const getData = useCallback(
    async ({ currentFilter, currentSortModel } = {}) => {
      try {
        setIsLoading(true);
        const fetchedData = await fetchData({ offset: rowsState.page, limit: rowsState.pageSize, filters: currentFilter ?? filtersRef.current, sortModel: currentSortModel ?? sortModel[0] });
        setData(fetchedData);
        setRowCount(+fetchedData[0]?.fullCount || 0);
        setIsLoading(false);
      } catch (error) {
        console.error(error.message);
        setData([]);
        setIsLoading(false);
        dispatch(displayErrorFeedback({ message: error.message || "Oops, an error occurred..." }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData, rowsState.page, rowsState.pageSize, sortModel, dispatch]
  );

  const getNewData = () => {
    setRowsState((prev) => ({ ...prev, page: 0 }));
    getData();
  };

  const addData = async (newData) => {
    try {
      const fetchedData = await addToList(newData);
      dispatch(displaySuccessFeedback({ message: fetchedData.message || "Success." }));
      await getData();
    } catch (error) {
      console.error(error.message);
      dispatch(displayErrorFeedback({ message: error.message || "Oops, an error occurred..." }));
    }
  };

  const deleteData = async (dataId) => {
    try {
      const fetchedData = await deleteFromList(dataId);
      dispatch(displaySuccessFeedback({ message: fetchedData.message || "Success." }));
      await getData();
    } catch (error) {
      console.error(error.message);
      dispatch(displayErrorFeedback({ message: error.message || "Oops, an error occurred..." }));
    }
  };

  const updateData = async (newData) => {
    try {
      const fetchedData = await updateList(newData);
      dispatch(displaySuccessFeedback({ message: fetchedData.message || "Success." }));
      await getData();
    } catch (error) {
      console.error(error.message);
      dispatch(displayErrorFeedback({ message: error.message || "Oops, an error occurred..." }));
    }
  };

  const resetSearch = () => {
    setFilters({});
    getData({ currentFilter: {} });
  };

  useEffect(() => {
    getData();
  }, [sortModel, getData, rowsState.page, rowsState.pageSize]);

  const openPopinAdd = () => {
    const itemData = headCells.reduce((acc, val) => {
      return { ...acc, [val.field]: val.isSelectMultiple ? [] : null };
    }, {});
    setPopin({ isOpen: true, action: addData, title: `Add ${label}`, itemData });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openPopinUpdate = (row) => {
    setPopin({ isOpen: true, action: updateData, title: `Update ${label}`, itemData: row });
  };

  const dataToDisplay = useMemo(() => {
    return [...headCellList.filter((cell) => (cell.isSuperAdminEditOnly ? user.role === roles.SUPERADMIN || cell.isOnTable : cell.isOnTable)), getActionCells({ user, roles, permissions, headCellList, openPopinUpdate, label })];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headCellList, user, roles, permissions, openPopinUpdate, label]);

  const onCellClick = useCallback(
    (cell) => {
      if (cell.field === "edit") {
        openPopinUpdate(cell.row);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openPopinUpdate]
  );

  useEffect(() => {
    const selectedRows = apiRef.current.getSelectedRows();
    const selectedIds = Array.from(selectedRows.keys());
    setSelectedIds(selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel]);

  const redirectToPreviewsPage = async (isAnonymous) => {
    const hashedId = await getHashedIds({ previewIdList: selectedIds.join(","), agencyId: user.agencyId, isAnonymous });
    if (!hashedId) return;
    const url = `/previews?id=${hashedId}`;
    const newPage = window.open(url, "_blank");
    newPage.focus();
  };

  const handleSelectionModel = (newSelectionModel) => {
    setRowSelectionModel(newSelectionModel);
  };

  const handleSortModelChange = async (sortModelData) => {
    setSortModel(sortModelData);
    getData({ currentSortModel: sortModelData[0] });
  };

  const handleRowsState = (id, value) => {
    setRowsState((prev) => ({ ...prev, [id]: value }));
  };

  const handlePageChange = (pageOption) => {
    setRowsState(pageOption);
  };

  const checkBoxSelection = label === "project";
  return (
    <Container>
      {/* {label === "project" && <SavedSearchPopin isOpen={isOpenPopinSearch} handleClose={handleSavedSearchesPopin.bind(null, false)} setFilters={setFilters} />} */}
      <PopinForm tableId={tableId} headCells={headCells} isOpen={popin.isOpen} title={popin.title} action={popin.action} handleClose={() => setPopin({ ...popin, isOpen: false })} itemData={popin.itemData} deleteData={deleteData} />
      <SubContainer>
        {hasFilter && <Filters headCells={headCellList} getData={getData} setFilters={setFilters} filters={filters} handleFiltersList={handleFiltersList} />}
        <Toolbar
          // handleSavedSearchesPopin={handleSavedSearchesPopin}
          openPopinAdd={openPopinAdd}
          hasFilter={hasFilter}
          rowSelectionModel={selectedIds}
          redirectToPreviewsPage={redirectToPreviewsPage}
          hasActiveFilter={!!Object.values(filters).some((value) => value !== null && value !== undefined && value !== "")}
          tableId={tableId}
          // saveSearch={saveSearch}
          resetSearch={resetSearch}
          getData={getData}
          handleRowsState={handleRowsState}
          label={label}
          getNewData={getNewData}
        />
        <StyledDataGrid
          apiRef={apiRef}
          onPaginationModelChange={handlePageChange}
          onCellClick={onCellClick}
          columns={dataToDisplay}
          rows={data}
          paginationModel={rowsState}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableColumnFilter
          checkboxSelection={checkBoxSelection}
          isRowSelectable={({ row }) => row.canShare && !!row.previewLink}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleSelectionModel}
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
          }}
        />
      </SubContainer>
    </Container>
  );
}
